import React, { useEffect, useState } from "react";
import { FormDateInput, FormCheckbox, FormRadio } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { NotificationInline } from 'components/notifications';
import moment from "moment";
import { useDirtyData } from "hooks/useDirtyData";
import { GridLayout } from 'components/grid/GridLayout';
import { useValidation } from "hooks/useValidation";
import { useEventWizard } from "pages/asset/events/shared/EventWizardContext";
import { formatDate, nullableDateToMoment } from "pages/asset/events/shared/utils";

export function ResheduleEvent({}) {

    const { 
        eventType,
        eventOptions, 
        eventStatus, 
        eventUpdateRequest, 
        originalEventUpdateRequest, 
        stepsConfig, 
        onEventUpdateRequestChange,
        setStepIsValid, 
        setEventStatus, 
        loadingData, 
        saving, 
        specialEventTypeRestricted,
        setActiveStep,
        setCanSave
    } = useEventWizard();

    const [stepData, setStepData] = useState(null);

    const isStepDataDirty = useDirtyData({
        eventDate: eventUpdateRequest.eventDate,
        eventStatus: eventUpdateRequest.eventStatus,
        moveAssociatedFutureEvents: eventUpdateRequest.moveAssociatedFutureEvents,
        vorStartDate: eventUpdateRequest.vorStartDate,
        vorEndDate: eventUpdateRequest.vorEndDate
    }, stepData);

    const [isValid, errors, validate] = useValidation();

    const dateFiveYears = 5;
    const minDateDefault = moment().subtract(dateFiveYears, 'years').toDate();
    const maxDateDefault = moment().add(dateFiveYears, 'years').toDate();

    const config = stepsConfig.ResheduleEvent;
    const { rules } = config;

    const isLoading = saving || loadingData || !stepData;

    const scheduleEventStatues = [
        { value: "PLANNED", label: "Planned" },
        { value: "SCHEDULED", label: "Scheduled" }
    ];

    const [selectedEventStatus, setSelectedEventStatus] = useState("PLANNED");

    useEffect(() => {
        setActiveStep("Reschedule");
    }, []);

    useEffect(() => {
        const statusesToSetSelectedStatusIn = ["PLANNED", "SCHEDULED"];

        if (statusesToSetSelectedStatusIn.includes(eventStatus?.key) && selectedEventStatus !== eventStatus?.key) { 
            setSelectedEventStatus(eventStatus?.key);
        }
    }, [eventStatus]);

    function onEventDateChange(value) {
        if (moment(value).isValid() && (stepData?.vorStartDate || stepData?.vorEndDate)) {
            var daysMoved = moment(value).diff(moment(stepData?.eventDate), 'days');
            if (isNaN(daysMoved)) {
                //Something odd has occurred so lets just set them the the Event Date and they can be changed if required
                onVorStartDateChange(moment(value))
                onVorEndDateChange(moment(value))
            } else {
                onVorStartDateChange(moment(stepData?.vorStartDate).add(daysMoved, 'days'))
                onVorEndDateChange(moment(stepData?.vorEndDate).add(daysMoved, 'days'))
            }
        }

        var eventDt = formatDate(value);
        //If this should always move future events (inially just PMI) then we set it if the date has changed.
        var moveEvents = config.alwaysMoveFutureEvents ? eventDt !== originalEventUpdateRequest.eventDate : eventUpdateRequest.moveAssociatedFutureEvents;

        setStepData((prev) => ({
            ...prev,
            eventDate: eventDt,
            moveAssociatedFutureEvents: moveEvents
        }));
    }

    function onMoveEventsChange(event) {

        setStepData((prev) => ({
            ...prev,
            moveAssociatedFutureEvents: event.target.checked
        }));
    }

    function onVorStartDateChange(value) {
        setStepData((prev) => ({
            ...prev,
            vorStartDate: formatDate(value)
        }));
    }

    function onVorEndDateChange(value) {
        setStepData((prev) => ({
            ...prev,
            vorEndDate: formatDate(value)
        }));
    }

    function onRadioFieldChange(item) {
        let scheduledStatus = eventOptions.eventStatuses.find(s => s.key === item.currentTarget.value);
        setSelectedEventStatus(scheduledStatus.key);
        onSetStatus(scheduledStatus);
    }

    function onSetStatus(newEventStatus) {
        setStepData((prev) => ({
            ...prev,
            eventStatus: newEventStatus.key
        }));

        if (newEventStatus && newEventStatus.key !== eventStatus.key) {
            setEventStatus(newEventStatus);
        }
    }

    useEffect(() => {
        if (!isStepDataDirty) {
            return;
        }

        validate(rules, stepData);
    }, [isStepDataDirty, stepData, rules]);

    useEffect(() => {
        setStepIsValid(isValid);

        if (isValid && stepData) {
            const canSave = stepData.eventStatus === "SCHEDULED" || stepData.eventStatus === "PLANNED";

            setCanSave(canSave);
            onEventUpdateRequestChange(stepData);
        }
    }, [isValid, stepData]);

    useEffect(() => {
        if (stepData === null && !!eventUpdateRequest.eventDate) {
            setStepData({
                eventDate: eventUpdateRequest.eventDate,
                moveAssociatedFutureEvents: config.alwaysMoveFutureEvents ? eventUpdateRequest.eventDate !== originalEventUpdateRequest.eventDate : eventUpdateRequest.moveAssociatedFutureEvents,
                eventStatus: eventUpdateRequest.eventStatus,
                vorStartDate: eventUpdateRequest.vorStartDate,
                vorEndDate: eventUpdateRequest.vorEndDate
            });
        }
    }, [eventUpdateRequest]);

    return (
        <div>
            <h2>Event Details</h2>
            <NotificationInline
                id="notification-event-helper"
                loading={loadingData}
                show={eventType?.eventHelperText && eventType.eventHelperText.length > 0}
                text={eventType.eventHelperText}
                allowClose={false}>
            </NotificationInline>

            <ShowHide
                evaluator={eventStatus?.key === "PLANNED" || (eventStatus?.key === "SCHEDULED" && !specialEventTypeRestricted)}
                show={
                    <FormRadio
                        disabled={saving}
                        options={scheduleEventStatues}
                        onChange={(item) => onRadioFieldChange(item)}
                        headingText="Event status"
                        selectedValue={selectedEventStatus}
                        loading={loadingData}
                        id="input-event-status-later"
                    />
                }
            />

            <FormDateInput
                id="event-date"
                disabled={isLoading}
                headingText={eventStatus?.startedStatus ? "Event Date" : eventStatus?.name + " Date"}
                onChange={onEventDateChange}
                value={formatDate(stepData?.eventDate)}
                loading={isLoading}
                dangerHelperText={errors.eventDate}
                minDate={minDateDefault}
                maxDate={maxDateDefault}
            />

            <ShowHide
                evaluator={!!(stepData && !eventType?.adHoc &&
                    originalEventUpdateRequest?.eventDate && stepData?.eventDate &&
                    originalEventUpdateRequest.eventDate !== stepData?.eventDate &&
                    !specialEventTypeRestricted)}
                show={(
                    <div className="spacer-bottom-large">
                        <ShowHide
                            evaluator={config.alwaysMoveFutureEvents}
                            show={(
                                <NotificationInline
                                    id="notification-move-events"
                                    text="This will move all future events."
                                    show={true}
                                    allowClose={false}>
                                </NotificationInline>
                            )}
                            hide={(
                                <FormCheckbox
                                    key="move-events"
                                    label="Move all future scheduled events of this type for this asset?"
                                    checked={stepData?.moveAssociatedFutureEvents}
                                    onChange={onMoveEventsChange}
                                    disabled={loadingData || saving}
                                />
                            )}
                        />
                    </div>
                )}
            />
            <ShowHide
                evaluator={!!(eventType.vorTimer || (eventType.vorTimerFailedOnly && eventUpdateRequest.failed))}
                show={(
                    <GridLayout numberOfColumns={2} gap={0}>
                        <FormDateInput
                            id="vor-start-date"
                            disabled={isLoading}
                            headingText="VOR Start Date"
                            onChange={onVorStartDateChange}
                            value={formatDate(stepData?.vorStartDate)}
                            loading={isLoading}
                            dangerHelperText={errors.vorStartDate}
                            minDate={nullableDateToMoment(stepData?.eventDate, "2000-01-01").subtract(50, 'days').toDate()}
                            maxDate={nullableDateToMoment(stepData?.eventDate, "2100-01-01").toDate()}
                        ></FormDateInput>

                        <FormDateInput
                            id="vor-end-date"
                            disabled={isLoading}
                            headingText="VOR End Date"
                            onChange={onVorEndDateChange}
                            value={formatDate(stepData?.vorEndDate)}
                            loading={isLoading}
                            dangerHelperText={errors.vorEndDate}
                            minDate={nullableDateToMoment(stepData?.eventDate, "2000-01-01").toDate()}
                            maxDate={nullableDateToMoment(stepData?.eventDate, "2100-01-01").add(50, 'days').toDate()}
                        ></FormDateInput>
                    </GridLayout>
                )}
            />
        </div>
    );
}

ResheduleEvent.propTypes = {
};